import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  IconButton,
  Link as MLink,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { DeleteTwoTone, HomeTwoTone } from '@mui/icons-material';
import React, { useContext, useEffect, useState } from 'react';
import { formattedDate } from '../utils';
import { IGuideTourMedicalAppointment } from '../utils/types';
import { AddEditGuidanceTourHealthMedicineAndDialysisReportDialog } from '../components/AddEditGuidanceTourHealthMedicineAndDialysisReportDialog';
import {
  deleteGuideTourHealthDialysis,
  deleteMedicalAppointment,
  guideTourHealthCustomerDialysisFile,
  guideTourMedicalAppointmentList,
} from '../api';
import { AuthContext } from '../context/AuthContext';
import { AddEditGuidanceTourHealthMedicalAppointmentDialog } from '../components/AddEditGudanceTourHealthDialysisMedicalAppoinmentDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import LoadingSpinner from '../components/LoadingSpinner';
import { IGuideTourHealthMedicineInfo } from './GuidanceTourHealthMedicinePage';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import PdfViewer from '../components/PdfViewer';

export function GuidanceTourHealthDialysisPage() {
  const { t } = useTranslation();
  const { token } = useContext(AuthContext);
  const { salesIndividualId } = useParams();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const tourId = searchParams.get('tourId');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState<number | null>(
    null,
  );
  const [customerIdToDelete, setCustomerIdToDelete] = useState<number | null>(
    null,
  );
  const showSnackbar = useAlertSnackbar();

  const [medicalAppointment, setMedicalAppointment] = useState<
    IGuideTourMedicalAppointment[]
  >([]);

  const [data, setData] = useState<IGuideTourHealthMedicineInfo[]>([]);
  const [openDialysisReportDialog, setOpenDialysisReportDialog] =
    useState<boolean>(false);
  const [openMedicalAppointmentDialog, setOpenMedicalAppointmentDialog] =
    useState<boolean>(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');

  const [deleteDialysisFileConfirmOpen, setDeleteDialysisFileConfirmOpen] =
    useState(false);
  const [selectedDoc, setSelectedDoc] = useState<{
    documentId?: number;
    customerId?: number;
  } | null>(null);

  const handleSaveDialysisReport = async () => {
    setOpenDialysisReportDialog(false);
    await fetchGuideTourHealthDialysisFile(salesIndividualId);
  };
  const handleDialysisReportDialogClose = async () => {
    setOpenDialysisReportDialog(false);
  };

  const handleAddDialysisReport = async () => {
    setOpenDialysisReportDialog(true);
  };

  const handleMedicalAppointmentDialogClose = async () => {
    setOpenMedicalAppointmentDialog(false);
    await fetchGuideTourHealthMedicalAppointment(salesIndividualId);
  };

  const handleAddMedicalAppointment = async () => {
    setOpenMedicalAppointmentDialog(true);
  };

  useEffect(() => {
    fetchGuideTourHealthMedicalAppointment(salesIndividualId);
    fetchGuideTourHealthDialysisFile(salesIndividualId);
  }, [salesIndividualId]);

  const fetchGuideTourHealthMedicalAppointment = async (id?: string) => {
    setLoading(true);
    try {
      const res = await guideTourMedicalAppointmentList(id, token);
      const data = await res.json();
      if (res?.status === 200) {
        setMedicalAppointment(data);
      } else {
        throw data;
      }
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDeleteMedicalAppointment = async (
    id: number,
    customerId: number,
  ) => {
    try {
      const res = await deleteMedicalAppointment(
        id,
        salesIndividualId,
        token,
        customerId,
      );

      if (!res.ok) {
        const errorResponse = await res.json();
        throw new Error(
          errorResponse.detail || `Request failed with status ${res.status}`,
        );
      }

      return res;
    } catch (e: any) {
      const errorMessage =
        e.message || t('accountContainer.deleteErrorMessage');
      showSnackbar(errorMessage, 'error');
    }
  };

  const fetchGuideTourHealthDialysisFile = async (
    guideSalesIndividualId?: string,
  ) => {
    setLoading(true);
    try {
      const res = await guideTourHealthCustomerDialysisFile(
        token,
        guideSalesIndividualId,
      );
      const data = await res.json();
      if (res?.status === 200) {
        setData(data);
      } else {
        throw data;
      }
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (customerId: number, id: number) => {
    setAppointmentToDelete(id);
    setCustomerIdToDelete(customerId);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (appointmentToDelete !== null && customerIdToDelete !== null) {
      await fetchDeleteMedicalAppointment(
        appointmentToDelete,
        customerIdToDelete,
      );
      setConfirmDialogOpen(false);
      setAppointmentToDelete(null);
      setCustomerIdToDelete(null);
      await fetchGuideTourHealthMedicalAppointment(salesIndividualId);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setAppointmentToDelete(null);
  };
  const handleCancelDeleteDialysisFileConfirm = () => {
    setDeleteDialysisFileConfirmOpen(false);
  };

  if (!salesIndividualId) return null;

  const handleViewFile = (documentPath: string) => {
    const fullPath = `https://portal.hisarturizm.com.tr${documentPath}`;
    setPdfUrl(fullPath);
    setPdfOpen(true);
  };

  const handleDeleteDialysisFileConfirmed = async () => {
    if (!selectedDoc) return;
    await deleteGuideTourHealthDialysisFile(
      selectedDoc.documentId,
      salesIndividualId,
      selectedDoc.customerId,
    );
    await fetchGuideTourHealthDialysisFile(salesIndividualId);
    setDeleteDialysisFileConfirmOpen(false);
    setSelectedDoc(null);
  };

  const openDeleteDialog = (documentId?: number, customerId?: number) => {
    setSelectedDoc({ documentId, customerId });
    setDeleteDialysisFileConfirmOpen(true);
  };
  const deleteGuideTourHealthDialysisFile = async (
    documentId?: number,
    guideSalesIndividualId?: string,
    customerId?: number,
  ) => {
    try {
      const res = await deleteGuideTourHealthDialysis(
        documentId,
        token,
        guideSalesIndividualId,
        customerId,
      );

      if (!res.ok) {
        const errorResponse = await res.json();
        throw new Error(
          errorResponse.detail || `Request failed with status ${res.status}`,
        );
      }
      return await res;
    } catch (e: any) {
      const errorMessage =
        e.message || t('accountContainer.creteAccountErrorMessage');
      showSnackbar(errorMessage, 'error');
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('guidanceTourHealth.dialysisReportDialog.dialysisTitle')}
      </Typography>

      <Breadcrumbs sx={{ mb: 2 }}>
        <MLink
          component={Link}
          to="/"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeTwoTone />
        </MLink>
        <MLink component={Link} to="/account">
          {t('nav.account')}
        </MLink>
        <MLink to="/account/guidance" component={Link}>
          <Typography>{t('nav.guidance')}</Typography>
        </MLink>
        <MLink
          component={Link}
          to={`/account/guidance/${salesIndividualId}?tourId=${tourId}`}
        >
          {t('nav.tourId', { tourId })}
        </MLink>
        <MLink
          component={Link}
          to={`/account/guidance/${salesIndividualId}/health?tourId=${tourId}`}
        >
          {t('guidanceTourHealth.title', { tourId })}
        </MLink>
        <Typography>
          {t('guidanceTourHealth.dialysisReportDialog.dialysisTitle')}
        </Typography>
      </Breadcrumbs>
      {loading ? (
        <LoadingSpinner loadingText={t('balancesQueryPage.loading')} />
      ) : (
        <Card>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',

              px: 2,
              pt: 1,
            }}
          >
            <Typography variant="h5">
              {t('guidanceTourHealth.dialysisReportDialog.dialysisReports')}
            </Typography>
            <Button
              onClick={() => handleAddDialysisReport()}
              size="small"
              color="secondary"
              variant="contained"
            >
              {t('guidanceTourHealth.add')}
            </Button>
          </Box>
          <List>
            {data.map((item, index) => (
              <ListItem
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 16px',
                  mt: index !== 0 ? -4 : -2,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1">
                    {item.customerFirstName} {item.customerLastName}
                  </Typography>
                  <Button onClick={() => handleViewFile(item.documentPath)}>
                    {t(
                      'guidanceTourHealth.dialysisReportDialog.viewDocumentPath',
                    )}
                  </Button>
                </CardContent>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    color="error"
                    onClick={() =>
                      openDeleteDialog(item.documentId, item.salesIndividualId)
                    }
                    aria-label="delete"
                  >
                    <DeleteTwoTone />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </Card>
      )}
      <Card sx={{ mt: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            pt: 1,
          }}
        >
          <Typography variant="h5">
            {t('guidanceTourHealth.dialysis')}
          </Typography>
          <Button
            onClick={() => handleAddMedicalAppointment()}
            size="small"
            color="secondary"
            variant="contained"
          >
            {t('guidanceTourHealth.add')}
          </Button>
        </Box>
        <List>
          {medicalAppointment.map((item, index) => (
            <ListItem
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 16px',
                mt: index !== 0 ? -4 : -2,
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1">
                  {item.firstName} {item.lastName}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  {item?.city}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {' '}
                  <Typography variant="subtitle2" color="textSecondary">
                    {formattedDate(item?.date)}
                  </Typography>
                  <Typography variant="subtitle2" color="textSecondary">
                    {item?.time}
                  </Typography>
                </Box>
              </CardContent>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  color="error"
                  onClick={() =>
                    handleDeleteClick(item?.salesIndividualId, item.id)
                  }
                  aria-label="delete"
                >
                  <DeleteTwoTone />
                </IconButton>
              </Box>
            </ListItem>
          ))}
        </List>
      </Card>
      <PdfViewer
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        pdfUrl={pdfUrl}
      />
      <AddEditGuidanceTourHealthMedicineAndDialysisReportDialog
        open={openDialysisReportDialog}
        onClose={handleDialysisReportDialogClose}
        onSave={handleSaveDialysisReport}
        guideSalesIndividualId={salesIndividualId}
        isDialysis={true}
      />
      <AddEditGuidanceTourHealthMedicalAppointmentDialog
        open={openMedicalAppointmentDialog}
        onClose={handleMedicalAppointmentDialogClose}
        onSave={async () => {
          setOpenMedicalAppointmentDialog(false);
        }}
        guideSalesIndividualId={salesIndividualId}
        dialysisCustomerList={data}
      />
      <ConfirmDialog
        open={confirmDialogOpen}
        title={t(
          'guidanceTourHealth.dialysisReportDialog.deleteMedicalAppointments',
        )}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      <ConfirmDialog
        open={deleteDialysisFileConfirmOpen}
        title={t(
          'guidanceTourHealth.dialysisReportDialog.deleteDialysisFileTitle',
        )}
        description={t(
          'guidanceTourHealth.dialysisReportDialog.deleteDialysisFileDescription',
        )}
        onConfirm={handleDeleteDialysisFileConfirmed}
        onCancel={handleCancelDeleteDialysisFileConfirm}
      />
    </Box>
  );
}
